import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { SqlComposer } from 'src/app/utils/query/sql-composer';
import {
    BiVizRealtimeCloudSaveCommitsDatasource,
    BiVizRealtimeCloudSaveGenericDatasource,
} from '../sql-maps/biviz-realtime-cloudsave-generic-sql-map';

interface StatsMetric {
    sql: string;
    alias: string;
    /** If true, the metric can be normalized **/
    normalizable?: boolean;
}

export interface RealtimeCloudSaveStatsDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
    metricsDatasource: any;
    statsMetrics: StatsMetric[];
    ignoredDsColumns?: DatasourceColumnId[];
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class RealtimeCloudSaveStatsDataLoaderFactory extends Factory<
    DataLoader,
    RealtimeCloudSaveStatsDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'RealtimeCloudSaveStatsDataLoaderFactory';

    getInstance(options: RealtimeCloudSaveStatsDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            // cloone queryContext to avoid modifying the original object
            queryContext = structuredClone(queryContext);

            // ignore columns that are not supported in this query
            const ignoredContextColumnIds = options.ignoredDsColumns ?? [];

            //filter out ignored columns from dsColumns.present
            queryContext.datasourceColumns =
                queryContext.datasourceColumns.filter(
                    (col) => !ignoredContextColumnIds.includes(col.id)
                );

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasource = options.metricsDatasource;

                // identify columns not supported in datasource
                const datasourceSeparated =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        datasource.sqlMap.columns
                    );

                let latestHourSqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_hours_up_to_latest is true, then we should filter hours to be up to the latest hour
                    if (queryContext.settings.realtimeHoursUpToLatest) {
                        const latest_hour = await DataLoader.runQuery(
                            `SELECT EXTRACT(HOUR FROM MAX(time_hour)) AS latest_hour FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                datasource.sqlMap.dataset,
                                datasource.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );

                        return {
                            where: [
                                BiVizRealtimeCloudSaveGenericDatasource.compileWhereSQLHoursUpToLatest(
                                    latest_hour.result[0].latest_hour
                                ),
                            ],
                        };
                    }
                    return;
                })();

                let currentWeekdaySqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_current_weekday is true, then we should filter weekday to be the current weekday
                    if (queryContext.settings.realtimeCurrentWeekday) {
                        const current_weekday = await DataLoader.runQuery(
                            `SELECT FORMAT_DATETIME("%A", MAX(time_hour)) AS current_weekday FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                datasource.sqlMap.dataset,
                                datasource.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );
                        queryContext.settings.realtimeCurrentWeekday =
                            current_weekday.result[0].current_weekday;

                        return {
                            where: [
                                BiVizRealtimeCloudSaveGenericDatasource.compileWhereSQLCurrentWeekday(
                                    current_weekday.result[0].current_weekday
                                ),
                            ],
                        };
                    }
                    return;
                })();

                const customSQLTokens: Partial<ICompiledSQLTokens> =
                    mergeSqlTokensArray(
                        [latestHourSqlToken, currentWeekdaySqlToken]
                            // filter out undefined values
                            .filter((x) => x) as Partial<ICompiledSQLTokens>[]
                    );

                const biVizRealtimeStatsDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        datasourceSeparated.missing
                    );

                // if missing exists, return early with status
                if (datasourceSeparated.missing.length > 0) {
                    this.emitter.emit({
                        data: this.latestData,
                        queryContext: queryContext,
                        // status: pendingDataLoaderResultStatus,
                        status: biVizRealtimeStatsDsColumnsStatus.pendingDataLoaderResultStatus,
                        statusMessage:
                            biVizRealtimeStatsDsColumnsStatus.pendingStatusMessage,
                        isLoading: false,
                        sqlAndJsProcessing: '',
                    });
                    return;
                }

                const statsCompiledSQL = datasource.compileSQLStatements(
                    datasourceSeparated.present,
                    queryContext.settings,
                    options,
                    customSQLTokens
                );

                const metricNormalizerDatasource =
                    BiVizRealtimeCloudSaveCommitsDatasource;

                const metricNormalizerDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        metricNormalizerDatasource.sqlMap.columns
                    );

                const metricNormalizerCompiledSQL =
                    metricNormalizerDatasource.compileSQLStatements(
                        metricNormalizerDsColumns.present,
                        queryContext.settings,
                        options,
                        customSQLTokens
                    );

                const metricNormalizerQuery = () => {
                    const normalizerDauMetricQuery = `COUNT( DISTINCT user_id)`;
                    const normalizerNonAnonymousMetricQuery = `COUNT( DISTINCT IF( is_anonymous != 'true', user_id, NULL))`;

                    switch (
                        queryContext.settings[
                            QueryContextSettingID
                                .REALTIME_CLOUDSAVE_NORMALIZE_METRICS
                        ]
                    ) {
                        case 'none':
                            return '1';
                        case 'dau':
                            return normalizerDauMetricQuery;
                        case 'non_anonymous':
                            return normalizerNonAnonymousMetricQuery;
                        default:
                            throw new Error(
                                `Unknown normalization mode: ${
                                    queryContext.settings[
                                        QueryContextSettingID
                                            .REALTIME_CLOUDSAVE_NORMALIZE_METRICS
                                    ]
                                }`
                            );
                    }
                };

                /**
                 * @returns SQL expression for metric, possibly normalized
                 */
                const metricSQL = (
                    metricSQLSource: string,
                    metric: StatsMetric,
                    // normalizer: string,
                    normalizedMode: IQueryContext['settings'][QueryContextSettingID.REALTIME_CLOUDSAVE_NORMALIZE_METRICS]
                ): string => {
                    if (metric.normalizable === false) {
                        // if the metric is marked as non-normalizable, return the raw metric SQL
                        return `${metricSQLSource}.${metric.alias}`;
                    }
                    switch (normalizedMode) {
                        case 'none':
                            return `${metricSQLSource}.${metric.alias}`;
                        case 'dau':
                        case 'non_anonymous':
                            return `SAFE_DIVIDE(${metricSQLSource}.${metric.alias}, normalizer.normalizer) AS ${metric.alias}`;
                        default:
                            throw new Error(
                                `Unknown normalization mode: ${normalizedMode}`
                            );
                    }
                };
                const statsMetrics: StatsMetric[] = options.statsMetrics;

                const statsQuery = `
                SELECT 
                  ${statsCompiledSQL.splitSelect}
                  ${statsCompiledSQL.datetimeTrunc()} AS datetime,
                  ${statsMetrics
                      .map(
                          (metric) =>
                              `NULLIF( ${metric.sql}, 0) AS ${metric.alias}`
                      )
                      .join(',\n')}
                FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasource.sqlMap.dataset,
                    datasource.sqlMap.tableId
                )}\` ds
                ${statsCompiledSQL.where}
                ${SqlComposer.GROUP_BY([
                    statsCompiledSQL.splitGroup,
                    `datetime`,
                ])}
                `;

                const normalizedDataQuery = `
                SELECT 
                  ${metricNormalizerCompiledSQL.splitSelect}
                  ${metricNormalizerCompiledSQL.datetimeTrunc()} AS datetime,
                  NULLIF( ${metricNormalizerQuery()}, 0) AS normalizer
                FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    metricNormalizerDatasource.sqlMap.dataset,
                    metricNormalizerDatasource.sqlMap.tableId
                )}\` ds
                ${metricNormalizerCompiledSQL.where}
                ${SqlComposer.GROUP_BY([
                    statsCompiledSQL.splitGroup,
                    `datetime`,
                ])}
                `;

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH stats AS
(
    ${statsQuery}
)

, normalizer AS
(
    ${normalizedDataQuery}
)

SELECT
  ${statsCompiledSQL.splitSelect ? 'stats.split_group, ' : ''}
  stats.datetime,
  ${statsMetrics
      .map((metric) => {
          return `${metricSQL(
              'stats',
              metric,
              queryContext.settings[
                  QueryContextSettingID.REALTIME_CLOUDSAVE_NORMALIZE_METRICS
              ] ?? 'none'
          )}`;
      })
      .join(',\n')}  
  , normalizer.normalizer
FROM stats
LEFT JOIN normalizer
ON stats.datetime = normalizer.datetime
${
    metricNormalizerCompiledSQL.splitGroupBy
        ? `AND stats.split_group = normalizer.split_group`
        : ''
}
${SqlComposer.ORDER_BY([statsCompiledSQL.splitGroup, `stats.datetime`])}
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'datetime',
                    queryContext.settings[
                        QueryContextSettingID.DATETIME_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    // status: pendingDataLoaderResultStatus,
                    status: biVizRealtimeStatsDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        biVizRealtimeStatsDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
